import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { patientApi, patientTokenStore } from '@/api/patientClient';
import type { PatientAuthResponse, PatientDto } from './types';

interface PatientSessionValue {
  patient: PatientDto | null;
  loading: boolean;
  isAuthenticated: boolean;
  sendOtp: (pharmacySlug: string, phone: string) => Promise<{ expiresAt: string }>;
  verifyOtp: (pharmacySlug: string, phone: string, code: string, name?: string) => Promise<PatientDto>;
  logout: () => void;
}

const PatientSessionContext = createContext<PatientSessionValue | null>(null);

export function PatientSessionProvider({ children }: { children: ReactNode }) {
  const [patient, setPatient] = useState<PatientDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientTokenStore.get()) {
      setLoading(false);
      return;
    }
    patientApi
      .get<PatientDto>('/api/patient-auth/me')
      .then(setPatient)
      .catch((err) => {
        if (err instanceof ApiError && err.isUnauthorized) patientTokenStore.clear();
      })
      .finally(() => setLoading(false));
  }, []);

  const sendOtp = useCallback(async (pharmacySlug: string, phone: string) => {
    return patientApi.post<{ expiresAt: string }>(`/api/patient-auth/${pharmacySlug}/send-otp`, { phone }, true);
  }, []);

  const verifyOtp = useCallback(async (pharmacySlug: string, phone: string, code: string, name?: string) => {
    const result = await patientApi.post<PatientAuthResponse>(
      `/api/patient-auth/${pharmacySlug}/verify-otp`,
      { phone, code, name },
      true,
    );
    patientTokenStore.set(result.token);
    setPatient(result.patient);
    return result.patient;
  }, []);

  const logout = useCallback(() => {
    patientTokenStore.clear();
    setPatient(null);
  }, []);

  const value = useMemo<PatientSessionValue>(
    () => ({ patient, loading, isAuthenticated: patient !== null, sendOtp, verifyOtp, logout }),
    [patient, loading, sendOtp, verifyOtp, logout],
  );

  return <PatientSessionContext.Provider value={value}>{children}</PatientSessionContext.Provider>;
}

// The hook and its provider are one cohesive unit; splitting them into two
// files for a fast-refresh nicety isn't worth the indirection — same call
// made for useSession/SessionProvider (see auth/session.tsx).
// eslint-disable-next-line react-refresh/only-export-components
export function usePatientSession(): PatientSessionValue {
  const context = useContext(PatientSessionContext);
  if (!context) throw new Error('usePatientSession must be used inside <PatientSessionProvider>.');
  return context;
}
